import { useEffect, useRef, useCallback } from 'react';

const SCROLL_KEY_PREFIX = 'bardplay-scroll-';

/**
 * Persists and restores scroll position per play+act+scene.
 * Saves to localStorage immediately; Supabase sync is a no-op placeholder
 * (the positions table doesn't exist yet, so we keep it localStorage-only).
 */
const useScrollPersistence = (playId, currentAct, currentScene, isReady) => {
  const scrollKey = playId ? `${SCROLL_KEY_PREFIX}${playId}-${currentAct}-${currentScene}` : null;
  const isSavingRef = useRef(false);
  const restoreAttemptedRef = useRef(false);

  // Save scroll position on scroll
  const handleScroll = useCallback(() => {
    if (!scrollKey || isSavingRef?.current) return;
    isSavingRef.current = true;
    // Debounce via rAF
    requestAnimationFrame(() => {
      try {
        const position = window.scrollY;
        localStorage.setItem(scrollKey, String(position));
      } catch {}
      isSavingRef.current = false;
    });
  }, [scrollKey]);

  // Attach scroll listener
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Restore scroll position when scene content is ready
  useEffect(() => {
    if (!isReady || !scrollKey) return;
    restoreAttemptedRef.current = false;

    const restore = () => {
      if (restoreAttemptedRef?.current) return;
      restoreAttemptedRef.current = true;
      try {
        const saved = localStorage.getItem(scrollKey);
        if (saved !== null) {
          const position = parseInt(saved, 10);
          if (!isNaN(position) && position > 0) {
            window.scrollTo({ top: position, behavior: 'smooth' });
          }
        }
      } catch {}
    };

    // Small delay to let the DOM render
    const timer = setTimeout(restore, 150);
    return () => clearTimeout(timer);
  }, [isReady, scrollKey]);

  const clearScrollPosition = useCallback(() => {
    if (!scrollKey) return;
    try {
      localStorage.removeItem(scrollKey);
    } catch {}
  }, [scrollKey]);

  return { clearScrollPosition };
};

export default useScrollPersistence;
